import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseResizeObserverOptions {
  minHeight?: number;
  headerOffset?: number;
}

export const useResizeObserver = (options: UseResizeObserverOptions = {}) => {
  const { minHeight = 400, headerOffset = 76 } = options;
  const [containerHeight, setContainerHeight] = useState<number>(minHeight);
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const updateHeight = useCallback(
    (element: HTMLDivElement) => {
      const rect = element.getBoundingClientRect();
      const availableHeight = window.innerHeight - rect.top - headerOffset;
      setContainerHeight(Math.max(availableHeight, minHeight));
    },
    [headerOffset, minHeight]
  );

  const containerRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (element !== null) {
        setNode(element);
        updateHeight(element);
      }
    },
    [updateHeight]
  );

  useEffect(() => {
    if (!node) return;

    const handleResize = () => {
      updateHeight(node);
    };

    window.addEventListener('resize', handleResize);

    resizeObserverRef.current = new ResizeObserver(() => {
      updateHeight(node);
    });

    resizeObserverRef.current.observe(node);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [node, updateHeight]);

  return { containerRef, containerHeight };
};
